/**
 * Green Flag — SparksChip
 *
 * The "3 Sparks Attending" pill. Green-tint bg, forest-ink text, bolt icon.
 * Optional overlapping attendee photos sit to the left of the chip. When
 * `locked` is set, a single lock-icon circle leads the stack to indicate
 * that some attendees are hidden until the user signs up.
 *
 * Two sizes:
 *   - sm (default) → 8px micro bold text (inside event cards)
 *   - md           → 12px (on the Game Night featured card)
 */

import {
  Box,
  HStack,
  Portal,
  PopoverBody,
  PopoverContent,
  PopoverPositioner,
  PopoverRoot,
  PopoverTrigger,
  Text,
  VStack,
  chakra,
  useRecipe,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useMediaBlob } from "../../lib/mediaHelpers";
import { useMeasuredVariantPath } from "../../lib/profileMedia";
import { Icon } from "../Icon/Icon";

export interface SparksChipInviter {
  /** Inviter's first name (e.g. "Steven"). */
  name: string;
  /** Authenticated media path for their photo — rendered un-blurred. */
  path?: string;
}

export interface SparksChipProps {
  count: number;
  size?: "sm" | "md";
  /** Authenticated media paths for attendee photos (e.g. `/123/Headshot/cropped.jpg`). */
  avatars?: string[];
  /** Show a lock circle leading the avatar stack. */
  locked?: boolean;
  /**
   * Mutual Sparks who invited the viewer to this event. When present, the chip
   * leads with "{name} invited you" and their photos un-blurred (they're
   * recognizable — the viewer already knows them), then falls back to the
   * anonymous "{count} other sparks attending" line for everyone else. The
   * server excludes these inviters from `count`, so no double-counting.
   */
  inviters?: SparksChipInviter[];
  /**
   * Make the chip tappable, opening a popover that explains what a Spark is.
   * On by default. The trigger stops click propagation, so chips living
   * inside a clickable card (EventCard, UpcomingEligibleWidget) open the
   * popover without also firing the card's onClick.
   */
  withTooltip?: boolean;
}

const SIZE_MAP = {
  sm: { avatar: 24, iconBox: "10px", overlap: -12 },
  md: { avatar: 32, iconBox: "14px", overlap: -18 },
} as const;

export function SparksChip({
  count,
  size = "sm",
  avatars,
  locked = false,
  inviters,
  withTooltip = true,
}: SparksChipProps) {
  const recipe = useRecipe({ key: "badge" });
  const styles = recipe({
    visual: size === "md" ? "sparksLarge" : "sparks",
  } as Parameters<typeof recipe>[0]);
  const s = SIZE_MAP[size];

  const chip = (
    <chakra.span css={styles}>
      <Icon name="spark-filled" boxSize={s.iconBox} />
      {count} {count === 1 ? "Spark" : "Sparks"} Attending
    </chakra.span>
  );

  // Inviter-led layout: "{name} invited you" with un-blurred inviter photos,
  // then the anonymous crowd as a smaller/muted "X other sparks attending".
  if (inviters?.length) {
    return (
      <VStack gap="1" align="flex-start">
        <HStack gap="0" align="center">
          {renderCircles({
            s,
            locked: false,
            avatars: inviters.slice(0, 3).map((i) => i.path ?? ""),
            blurred: false,
            trailing: (
              <Text as="span" textStyle="caption" color="fg.default">
                {inviterText(inviters.map((i) => i.name))}
              </Text>
            ),
          })}
        </HStack>
        {count > 0 &&
          maybeWithTooltip(
            <HStack gap="0" align="center">
              {renderCircles({
                s,
                locked,
                avatars,
                blurred: true,
                trailing: (
                  <chakra.span
                    display="inline-flex"
                    alignItems="center"
                    gap="1"
                    color="fg.muted"
                  >
                    <Icon name="spark-filled" boxSize={s.iconBox} />
                    <Text as="span" textStyle="inputLabel">
                      {count} other {count === 1 ? "spark" : "sparks"} attending
                    </Text>
                  </chakra.span>
                ),
              })}
            </HStack>,
            withTooltip
          )}
      </VStack>
    );
  }

  const hasCircles = locked || !!avatars?.length;
  const content = !hasCircles ? (
    chip
  ) : (
    <HStack gap="0" align="center">
      {renderCircles({ s, locked, avatars, chip })}
    </HStack>
  );

  return maybeWithTooltip(content, withTooltip);
}

// Wrap a chip in the tap-to-explain "What's a Spark?" popover. Returns the
// node unchanged when tooltips are disabled.
function maybeWithTooltip(content: ReactNode, withTooltip: boolean): ReactNode {
  if (!withTooltip) return content;
  return (
    <PopoverRoot positioning={{ placement: "bottom" }}>
      <PopoverTrigger asChild>
        <chakra.button
          type="button"
          appearance="none"
          bg="transparent"
          border="none"
          p="0"
          cursor="pointer"
          display="inline-flex"
          alignItems="center"
          // Chips can sit inside a clickable card (EventCard,
          // UpcomingEligibleWidget) — stop the tap from bubbling up and
          // navigating away, so it opens the popover instead.
          onClick={(e) => e.stopPropagation()}
        >
          {content}
        </chakra.button>
      </PopoverTrigger>
      {/* Portal the positioner to the document body so it escapes the
          card's transform / overflow / scroll context. Without this, a chip
          inside a framer-motion card (EventV2) or an overflow-clipped,
          scrollable card (EventCard, UpcomingEligibleWidget) mispositions —
          the popover flickers and jumps to a random spot on tap. */}
      <Portal>
        <PopoverPositioner>
          <PopoverContent maxW="18rem" bg="white" color="fg.default">
            <PopoverBody>
              <Text mb="2" fontWeight="medium" color="fg.default">
                What's a Spark? ⚡
              </Text>
              <Text as="div" fontSize="sm" color="fg.muted">
                It's someone you've liked who's coming too — so you'll walk in
                already knowing there's someone you're excited to meet.
              </Text>
            </PopoverBody>
          </PopoverContent>
        </PopoverPositioner>
      </Portal>
    </PopoverRoot>
  );
}

// Build the "{name} invited you" lead line. Names up to 3 are spelled out;
// 4+ collapses the tail into "and N others".
function inviterText(names: string[]): string {
  const n = names.length;
  if (n === 1) return `${names[0]} invited you`;
  if (n === 2) return `${names[0]} and ${names[1]} invited you`;
  if (n === 3) return `${names[0]}, ${names[1]} and ${names[2]} invited you`;
  const others = n - 2;
  return `${names[0]}, ${names[1]} and ${others} others invited you`;
}

// Avatar/lock circle stack that leads a chip. Shared by the anonymous crowd
// line (blurred) and the inviter lead line (un-blurred). `trailing` is the
// text/chip rendered after the circles; falls back to `chip` for callers that
// pass the pre-built pill.
function renderCircles({
  s,
  locked,
  avatars,
  chip,
  trailing,
  blurred = true,
}: {
  s: (typeof SIZE_MAP)[keyof typeof SIZE_MAP];
  locked: boolean;
  avatars?: string[];
  chip?: ReactNode;
  trailing?: ReactNode;
  blurred?: boolean;
}) {

  const circleBaseProps = {
    w: `${s.avatar}px`,
    h: `${s.avatar}px`,
    borderRadius: "full" as const,
    border: "2px solid",
    borderColor: "bg.canvas",
    display: "inline-flex" as const,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    flexShrink: 0,
    position: "relative" as const,
  };

  const avatarCount = avatars?.length ?? 0;
  const totalCircles = (locked ? 1 : 0) + avatarCount;
  const hasCircles = totalCircles > 0;
  let index = 0;
  return (
    <>
      {locked && (
        <Box
          as="span"
          {...circleBaseProps}
          bg="bg.muted"
          color="fg.default"
          ml={index++ === 0 ? "0" : `${s.overlap}px`}
          zIndex={totalCircles}
        >
          <Icon name="lock" boxSize={s.iconBox} />
        </Box>
      )}
      {avatars?.map((path, i) => (
        <AvatarCircle
          key={i}
          path={path}
          blurred={blurred}
          circleBaseProps={circleBaseProps}
          ml={index === 0 ? "0" : `${s.overlap}px`}
          zIndex={totalCircles - index++}
        />
      ))}
      {/* The pre-built pill tucks under the last circle (ml -2); a bare text
          trailing gets a small positive gap so it doesn't overlap. */}
      <Box ml={hasCircles ? (trailing ? "2" : "-2") : "0"}>{trailing ?? chip}</Box>
    </>
  );
}

// Single overlap circle in the SparksChip stack. Fetches the auth-gated
// avatar via useMediaBlob, picks a variant sized to the rendered circle, and
// (by default) applies a CSS blur — the anonymous crowd is decorative, faces
// aren't meant to be recognizable. `blurred={false}` renders the photo sharp,
// used for the viewer's own inviters (people they already know). `overflow:
// hidden` on the wrapper clips the blur halo to the circle so it doesn't
// bleed past neighbors. When there's no photo an un-blurred circle shows a
// user-icon fallback so an inviter without a headshot still reads as a person.
function AvatarCircle({
  path,
  circleBaseProps,
  ml,
  zIndex,
  blurred = true,
}: {
  path: string;
  circleBaseProps: Record<string, unknown>;
  ml: string;
  zIndex: number;
  blurred?: boolean;
}) {
  // Raw http(s) URLs bypass the auth-blob pipeline — used in Storybook and
  // any future direct-CDN avatar source.
  const isExternal = /^https?:/.test(path);
  const [setMeasureRef, variantPath] = useMeasuredVariantPath(
    isExternal ? undefined : path
  );
  const { blob } = useMediaBlob(isExternal ? "" : variantPath ?? "");
  const bg = isExternal
    ? `url(${path})`
    : blob
      ? `url(${URL.createObjectURL(blob)})`
      : undefined;
  return (
    <Box
      ref={setMeasureRef}
      as="span"
      data-testid="spark-avatar-circle"
      {...circleBaseProps}
      bg="bg.muted"
      color="fg.brand"
      ml={ml}
      zIndex={zIndex}
      overflow="hidden"
    >
      {bg ? (
        <Box
          as="span"
          position="absolute"
          // When blurred, inset by -blurRadius so the blur kernel always has
          // image to sample at the circle's edge — avoids a soft transparent
          // ring. Sharp photos fill the circle exactly.
          inset={blurred ? "-2px" : "0"}
          backgroundImage={bg}
          backgroundSize="cover"
          backgroundPosition="center"
          filter={blurred ? "blur(2px)" : undefined}
        />
      ) : (
        !blurred && <Icon name="user-solid" boxSize="60%" />
      )}
    </Box>
  );
}
